import { SAFE_POLLING_CONFIG, SAFE_SERVICE_URLS, SafeTransactionResponse } from 'config/safe'
import { Address, Hash, isAddress, PublicClient, TransactionReceipt } from 'viem'

const resolvedSafeTxCache = new Map<string, Hash>()
const inFlightSafeTx = new Map<string, Promise<Hash>>()
const safeWalletCache = new Map<string, boolean>()

const delay = (milliseconds: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, milliseconds)
  })

const SAFE_ABI = [
  {
    inputs: [],
    name: 'getOwners',
    outputs: [{ internalType: 'address[]', name: '', type: 'address[]' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const

export async function isSafeWallet(publicClient: PublicClient, address: Address): Promise<boolean> {
  const chainId = publicClient.chain?.id ?? (await publicClient.getChainId())
  const cacheKey = `${chainId}:${address.toLowerCase()}`

  const cached = safeWalletCache.get(cacheKey)
  if (cached !== undefined) return cached

  try {
    const bytecode = await publicClient.getCode({ address })
    if (!bytecode || bytecode === '0x') {
      safeWalletCache.set(cacheKey, false)
      return false
    }

    const owners = await publicClient.readContract({ address, abi: SAFE_ABI, functionName: 'getOwners' })

    const isSafe = Array.isArray(owners) && owners.length > 0 && owners.every((owner) => isAddress(owner))

    safeWalletCache.set(cacheKey, isSafe)
    return isSafe
  } catch (error) {
    console.error('Failed to detect safe wallet', error)
    safeWalletCache.set(cacheKey, false)
    return false
  }
}

export async function resolveSafeTxHash(chainId: number, safeTxHash: Hash): Promise<Hash> {
  const cacheKey = `${chainId}:${safeTxHash}`
  const cached = resolvedSafeTxCache.get(cacheKey)
  if (cached) return cached

  const existing = inFlightSafeTx.get(cacheKey)
  if (existing) return existing

  const serviceUrl = SAFE_SERVICE_URLS[chainId]
  if (!serviceUrl) {
    throw new Error(`Safe transaction service is not configured for chain ${chainId}`)
  }

  const url = `${serviceUrl}/api/v1/multisig-transactions/${safeTxHash}/`

  const resolvePromise = (async () => {
    let lastError: Error | undefined

    for (let attempt = 1; attempt <= SAFE_POLLING_CONFIG.MAX_ATTEMPTS; attempt++) {
      try {
        const response = await fetch(url)

        if (response.status === 429) {
          lastError = new Error('Safe transaction service rate limited')
          await delay(SAFE_POLLING_CONFIG.RATE_LIMIT_DELAY_MS)
          continue
        }

        if (response.status === 404) {
          // Not indexed yet.
        } else if (!response.ok) {
          lastError = new Error(`Safe transaction service error (${response.status})`)
        } else {
          const data = (await response.json()) as SafeTransactionResponse
          const resolvedHash = data.txHash ?? data.transactionHash

          if (data.txStatus) {
            if (data.txStatus === 'FAILED' || data.txStatus === 'CANCELLED') {
              throw new Error(`Safe transaction ${data.txStatus.toLowerCase()}`)
            }

            if (data.txStatus === 'SUCCESS' && resolvedHash) {
              return resolvedHash
            }
          } else if (typeof data.isExecuted === 'boolean') {
            if (data.isExecuted && data.isSuccessful === false) {
              throw new Error('Safe transaction failed')
            }

            if (data.isExecuted && data.isSuccessful && resolvedHash) {
              return resolvedHash
            }
          }
        }
      } catch (error) {
        const resolvedError = error instanceof Error ? error : new Error('Failed to resolve Safe transaction hash')

        if (
          resolvedError.message === 'Safe transaction failed' ||
          resolvedError.message === 'Safe transaction cancelled'
        ) {
          throw resolvedError
        }

        lastError = resolvedError
      }

      if (attempt < SAFE_POLLING_CONFIG.MAX_ATTEMPTS) {
        await delay(SAFE_POLLING_CONFIG.INTERVAL_MS)
      }
    }

    if (lastError) {
      throw lastError
    }

    throw new Error('Safe transaction was not executed')
  })()

  inFlightSafeTx.set(cacheKey, resolvePromise as Promise<Hash>)
  try {
    const resolved = await resolvePromise
    resolvedSafeTxCache.set(cacheKey, resolved as Hash)
    return resolved
  } finally {
    inFlightSafeTx.delete(cacheKey)
  }
}

type WaitForTransactionReceiptArgs = {
  hash: Hash
  address?: Address
  confirmations?: number
}

/**
 * Waits for a transaction receipt, automatically handling both EOA and Safe wallets.
 * For Safe wallets, resolves the safeTxHash to the actual on-chain transaction hash.
 */
export async function waitForTransactionReceipt(
  publicClient: PublicClient,
  { hash, address, confirmations = 1 }: WaitForTransactionReceiptArgs
): Promise<TransactionReceipt> {
  try {
    return await publicClient.getTransactionReceipt({ hash })
  } catch (error) {
    // Log unexpected errors, but continue to Safe resolution for "not found"
    if (error instanceof Error && !error.message.toLowerCase().includes('not found')) {
      console.warn('Unexpected error fetching receipt, trying Safe resolution:', error.message)
    }
    // Receipt is not available yet - will try Safe wallet detection
  }

  if (address) {
    const isSafe = await isSafeWallet(publicClient, address)
    if (isSafe) {
      const chainId = publicClient.chain?.id ?? (await publicClient.getChainId())
      const resolvedHash = await resolveSafeTxHash(chainId, hash)

      try {
        return await publicClient.getTransactionReceipt({ hash: resolvedHash })
      } catch {
        // Receipt may not be indexed yet.
      }

      return publicClient.waitForTransactionReceipt({
        hash: resolvedHash,
        confirmations,
      })
    }
  }

  return publicClient.waitForTransactionReceipt({ hash, confirmations })
}
